import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, Save, AlertTriangle, Clock, Bell, UserCheck } from 'lucide-react';

interface NotificationSettings {
  id?: string;
  clinic_id: string;
  email_enabled: boolean;
  frequency: 'immediate' | 'daily' | 'weekly';
  notify_overdue: boolean;
  notify_due_soon: boolean;
  notify_pending_ack: boolean;
  notify_policy_published: boolean;
  notify_team_changes: boolean;
}

const DEFAULT_SETTINGS: Omit<NotificationSettings, 'clinic_id'> = {
  email_enabled: true,
  frequency: 'immediate',
  notify_overdue: true,
  notify_due_soon: true,
  notify_pending_ack: true,
  notify_policy_published: true,
  notify_team_changes: false,
};

const NOTIFICATION_ITEMS = [
  {
    key: 'notify_overdue' as const,
    icon: AlertTriangle,
    iconColor: 'text-destructive',
    label: 'Overdue Policy Reviews',
    description: 'Alert when a policy review is past its due date',
  },
  {
    key: 'notify_due_soon' as const,
    icon: Clock,
    iconColor: 'text-[hsl(var(--status-due-soon))]',
    label: 'Upcoming Reviews',
    description: 'Reminder 7 days, 3 days, and 1 day before a review is due',
  },
  {
    key: 'notify_pending_ack' as const,
    icon: UserCheck,
    iconColor: 'text-primary',
    label: 'Pending Acknowledgments',
    description: "Remind staff who haven't acknowledged a published policy",
  },
  {
    key: 'notify_policy_published' as const,
    icon: Bell,
    iconColor: 'text-[hsl(var(--header-to))]',
    label: 'New Policy Published',
    description: 'Notify staff when a new policy requires their acknowledgment',
  },
  {
    key: 'notify_team_changes' as const,
    icon: Bell,
    iconColor: 'text-muted-foreground',
    label: 'Team Changes',
    description: 'Alert owners when members are added or removed',
  },
];

export default function SettingsNotifications() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<NotificationSettings | null>(null);

  const { data: saved, isLoading } = useQuery({
    queryKey: ['notification-settings', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return null;
      const { data, error } = await (supabase as any)
        .from('notification_settings')
        .select('*')
        .eq('clinic_id', profile.clinic_id)
        .maybeSingle();
      if (error) throw error;
      return data as NotificationSettings | null;
    },
    enabled: !!profile?.clinic_id,
  });

  // Populate local state when data loads (or use defaults)
  useEffect(() => {
    if (profile?.clinic_id) {
      setSettings({
        clinic_id: profile.clinic_id,
        ...(saved ?? DEFAULT_SETTINGS),
      });
    }
  }, [saved, profile?.clinic_id]);

  const saveMutation = useMutation({
    mutationFn: async (values: NotificationSettings) => {
      if (!profile?.clinic_id) throw new Error('No clinic found');
      const { error } = await (supabase as any)
        .from('notification_settings')
        .upsert({ ...values, clinic_id: profile.clinic_id }, { onConflict: 'clinic_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      toast({ title: 'Notification preferences saved' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to save preferences',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  function toggle(key: keyof NotificationSettings) {
    setSettings(s => s ? { ...s, [key]: !s[key] } : s);
  }

  function handleSave() {
    if (settings) saveMutation.mutate(settings);
  }

  if (isLoading || !settings) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      {/* Section Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure email alerts for compliance events
        </p>
      </div>

      <div className="space-y-6">
        {/* Master Email Toggle */}
        <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-border bg-white dark:bg-background p-4">
          <div>
            <p className="text-sm font-medium text-foreground">Email Notifications</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {settings.email_enabled ? 'Active — sending compliance alerts' : 'Paused — no alerts will be sent'}
            </p>
          </div>
          <Switch
            checked={settings.email_enabled}
            onCheckedChange={() => toggle('email_enabled')}
          />
        </div>

        {/* Frequency */}
        <div className="space-y-1.5">
          <Label>Notification Frequency</Label>
          <Select
            value={settings.frequency}
            onValueChange={(v: string) =>
              setSettings(s => s ? { ...s, frequency: v as NotificationSettings['frequency'] } : s)
            }
          >
            <SelectTrigger className="border-gray-200 dark:border-border bg-white dark:bg-background text-left">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="immediate">
                <div>
                  <p className="font-medium">Immediate</p>
                  <p className="text-xs text-muted-foreground">Send alerts as events occur</p>
                </div>
              </SelectItem>
              <SelectItem value="daily">
                <div>
                  <p className="font-medium">Daily Digest</p>
                  <p className="text-xs text-muted-foreground">Summary every morning at 8 AM</p>
                </div>
              </SelectItem>
              <SelectItem value="weekly">
                <div>
                  <p className="font-medium">Weekly Digest</p>
                  <p className="text-xs text-muted-foreground">Summary every Monday morning</p>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Divider */}
        <div className="space-y-4">
          <p className="text-sm font-medium text-muted-foreground">
            Alert Types
          </p>

          {/* Individual Notification Toggles */}
          <div className="space-y-3">
            {NOTIFICATION_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.key} className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-border bg-white dark:bg-background p-3">
                  <div className="flex items-center gap-3">
                    <Icon className={`h-5 w-5 ${item.iconColor}`} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={!!settings[item.key]}
                    onCheckedChange={() => toggle(item.key)}
                    className="ml-4 shrink-0"
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-2">
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
            ) : (
              <><Save className="mr-2 h-4 w-4" /> Save Preferences</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
